export interface User {
  id: string;
  userName?: string;
  username?: string;
  fullName?: string;
  roles: string[];
  activated: boolean;
  createdAt: string;
  modifiedAt: string;
}

export interface Story {
  id: string;
  title: string;
  description: string;
  coverUrl?: string;
  status: 'draft' | 'published' | 'archived';
  categories: string[];
  gender: 'male' | 'female' | 'unisex';
  ongoing?: boolean;
  episodes?: Episode[];
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface EpisodeSubtitleInfo {
  url: string;
  label: string;
}

export interface Episode {
  id: string;
  storyId: string;
  chap: number;
  thumbnailUrl?: string;
  videoUrl?: string;
  duration?: number;
  status: 'draft' | 'uploading' | 'processing' | 'ready' | 'published' | 'error' | 'failed';
  isLocked: boolean;
  unlockCondition?: string;
  // Epic 8: Multi-language subtitles { "en": { url, label }, "vi": { url, label } }
  subtitles?: Record<string, EpisodeSubtitleInfo>;
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface Tag {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
}

export interface UploadStatus {
  id: string;
  episodeId: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
}

export interface QueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalRecord: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: {
    accessToken: string;
    refreshToken: string;
  };
  user: {
    username: string;
    fullName: string;
    roles: string[];
  };
}

export interface SourceStory {
  id: string;
  name: string;
  author?: string;
  link?: string;
  summary?: string;
  pros?: string;
  cons?: string;
  hook?: string;
  caption?: string;
  storyConcept?: string;
  targetAudiences?: any[];
  imageStyle?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface StoryVoice {
  id: string;
  sourceStoryId: string;
  code: string;
  displayName: string;
  description?: string;
  gender: 'MALE' | 'FEMALE';
  provider: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceChapter {
  id: string;
  sourceStoryId: string;
  orderIndex: number;
  name?: string;
  content?: string;
  link?: string;
  instruction?: string;
  outline?: string;
  summary?: string;
  aiReview?: any[];
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceChapterSuggestion {
  id: string;
  sourceStoryId: string;
  title: string;
  instruction: string;
  direction?: string;
  status: 'PENDING' | 'USED';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface OutlineReviewAudienceResult {
  audienceCode: string;
  audienceName: string;
  score: number;
  feedbacks: string[];
}

export interface OutlineReviewVersion {
  roundNumber: number;
  outline: string;
  averageScore: number;
  audienceReviews: OutlineReviewAudienceResult[];
}

export interface ContentReviewVersion {
  roundNumber: number;
  content: string;
  averageScore: number;
  audienceReviews: OutlineReviewAudienceResult[];
}

export interface SourceProcess {
  id: string;
  sourceStoryId: string;
  name: string;
  orderIndex: number;
  chapterIds: string[];
  characterIds?: string[];
  status: 'DRAFT' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  summary?: string;
  totalScenes?: number;
  completedScenes?: number;
  isContentComplete?: boolean;
  videoForm?: 'LONG' | 'SHORT';
  parentProcessId?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface Storyteller {
  id: string;
  sourceStoryId: string;
  name: string;
  personality: string;
  style: string;
  isActive: boolean;
  videoForm?: 'LONG' | 'SHORT';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceCharacter {
  id: string;
  sourceStoryId: string;
  processId?: string;
  name: string;
  description?: string;
  role?: string; // PROTAGONIST, ANTAGONIST, SUPPORTING, BACKGROUND
  avatarUrl?: string;
  localizedData?: {
    name?: string;
    [key: string]: any;
  };
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceCharacterCheckpoint {
  id: string;
  characterId: string;
  name: string;
  description?: string;
  visualPrompt?: string;
  avatarUrl?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ExtractedCharacter {
  id?: string;
  name: string;
  description: string;
  role: string;
  isNew: boolean;
  matchedCharacterId?: string;
  checkpoints?: Array<{
    name: string;
    description: string;
    reason: string;
    isNew: boolean;
    matchedCheckpointId?: string;
  }>;
}

export interface SourceScene {
  id: string;
  sourceStoryId: string;
  processId: string;
  orderIndex: number;
  title: string;
  summary?: string;
  setting?: string;
  characterIds?: string[]; // UUID array
  reason?: string;
  keyMoments?: string[];
  endState?: string;
  outfits?: SceneOutfit[];
  totalPanels?: number;
  activePanels?: number;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SceneOutfit {
  characterId: string;
  canInheritFromPrevScene: boolean;
  outfitDescription: string;
  technicalPrompt: string;
  imageUrl: string;
  note: string;
}

export interface ExtractedScene {
  title: string;
  summary: string;
  setting: string;
  characterIds: string[]; // UUID array (AI returns directly)
  reason: string;
  keyMoments: string[];
  endState: string;
}

export interface SourcePanel {
  id: string;
  sourceStoryId: string;
  processId: string;
  sceneId: string;
  sceneOrderIndex: number;
  characterIds: string[];
  caption: string;
  localizedCaption?: string;
  visualDescription: string;
  refinedVisualDescription?: string;
  technicalPrompt?: string;
  visualPlan?: any;
  characterCheckpoints?: Array<{ characterId: string; checkpointId: string }>;
  imagePath?: string;
  mediaType: 'IMAGE' | 'VIDEO';
  firstFrameUrl?: string;
  videoUrl?: string;
  videoDuration?: number;
  estimatedAudioDuration?: number;
  audioDuration?: number;
  motionPrompt?: string;
  multiShotConfig?: any; // JSONB field for storing AI shot config
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ExtractedPanel {
  title?: string;
  caption: string;
  visualDescription?: string;
  characterIds: string[];
  characterCheckpoints?: Array<{ characterId: string; checkpointId: string }>;
  isVideo?: boolean;
  estimatedAudioDuration?: number;
}

export interface PromptTemplate {
  id: string;
  code: string;
  name: string;
  description?: string;
  template: string;
  variables?: any;
  expectedOutput?: string;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceImageGeneration {
  id: string;
  ownerId: string;
  ownerType: 'CHARACTER' | 'PANEL' | 'SOURCE_CHARACTER_CHECKPOINT' | 'SCENE_OUTFIT';
  provider: 'GEMINI' | 'KLING';
  detail: {
    prompt: string;
    model?: string;
    aspectRatio?: string;
    [key: string]: any;
  };
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  error?: string;
  createdBy?: string;
  createdAt: string;
}

export interface SourceImage {
  id: string;
  ownerId: string;
  ownerType: 'CHARACTER' | 'PANEL' | 'SOURCE_CHARACTER_CHECKPOINT';
  sourceType: 'AI_GENERATION' | 'UPLOAD' | 'URL' | 'MANUAL_UPLOAD';
  sourceId?: string;
  url: string;
  filename?: string;
  contentType?: string;
  width?: number;
  height?: number;
  size?: number;
  createdAt: string;
}

// 🆕 Epic 6: Subtitle Types
export interface SubtitleStyle {
  backgroundColor?: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export interface SubtitleFile {
  url: string;
  language: string;
  languageName: string;
  status: 'generating' | 'ready' | 'error';
  generatedAt: string;
  fileSize?: number;
  entriesCount?: number;
  style?: SubtitleStyle;
  error?: string;
}

export interface SubtitlesData {
  defaultStyle: SubtitleStyle;
  files: Record<string, SubtitleFile>;
}

export interface SubtitleEntry {
  text: string;
  startTime: number;
  endTime: number;
  confidence: number;
}

export interface Hook {
  id: string;
  concept: string;
  panels: Array<{
    id: string;
    caption: string;
    imageUrl: string | null;
    videoUrl: string | null;
    mediaType: 'IMAGE' | 'VIDEO';
  }>;
  reviewScore: number;
  reviewData: Array<{
    audience: string;
    score: number;
    feedbacks: string[];
  }>;
  createdAt: string;
}

export interface SourceEpisode {
  id: string;
  sourceStoryId: string;
  name: string;
  orderIndex: number;
  startPanelId?: string;
  endPanelId?: string;
  thumbnailUrl?: string;
  masterAudioUrl?: string;
  audioStatus?: 'pending' | 'generating' | 'ready' | 'error';
  panels?: Array<{ 
    panelId: string; 
    orderIndex: number; 
    startTime?: number; 
    endTime?: number;
    confidence?: number;
    reasoning?: string;
    aiCaption?: string;
    imageUrl?: string;
    caption?: string;
    visualDescription?: string;
    motionEffects?: any[]; // Epic 3
    soundEffects?: any[];  // Epic 5
    subtitleEntries?: Record<string, SubtitleEntry[]>; // Epic 6
    // Epic 7: Video panel fields (enriched from SourcePanel DB)
    mediaType?: 'IMAGE' | 'VIDEO';
    videoUrl?: string;
    videoDuration?: number;
    firstFrameUrl?: string;
    motionPrompt?: string;
  }>;
  subtitles?: SubtitlesData; // 🆕 Epic 6
  hooks?: Hook[]; // 🆕 Epic 1 Phase 1
    audioChunks?: any[]; // 🆕 Epic 1 Phase 1
  metadata?: {
    processId: string;
    startChapterId: string;
    videoForm: 'LONG' | 'SHORT';
  } | null;
  status: 'DRAFT' | 'PUBLISHED';

  // Epic 3 V2: Video generation
  videoStatus?: string;
  videoUrl?: string;
  videoJobId?: string;
  videoError?: string;

  speedMultiplier?: number; // 🆕 Epic 1 Phase 1

  // Epic 5: Audio integration
  backgroundMusicId?: string;
  backgroundMusic?: AudioAsset;
  backgroundVolume?: number;

  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface ImageStyle {
  id: string;
  code: string;
  name: string;
  description?: string;
  illustrationUrl?: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourceGlossary {
  id: string;
  sourceStoryId: string;
  originalTerm: string;
  localizedTerm: string;
  type: 'LOCATION' | 'ORGANIZATION' | 'ITEM' | 'CONCEPT' | 'OTHER';
  description?: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface SourcePanelAudio {
  id: string;
  sourceStoryId: string;
  sourcePanelId: string;
  audioUrl: string;
  duration: number;
  status: 'active' | 'inactive';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export enum AdminTaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum AdminTaskType {
  AUTO_CHAPTER = 'AUTO_CHAPTER',
  AUTO_PROCESS = 'AUTO_PROCESS',
  AUTO_EPISODE = 'AUTO_EPISODE',
  AUTO_CLONE_PROCESS = 'AUTO_CLONE_PROCESS',
  FULL_AUTO_CHAPTER_TO_VIDEO = 'FULL_AUTO_CHAPTER_TO_VIDEO'
}

export interface FullAutoTaskInput {
  sourceStoryId: string;
  storyName: string;
  chapterName?: string;
  instruction?: string;
  outlinePassScore: number;
  maxOutlineRetries: number;
  contentPassScore: number;
  maxContentRetries: number;
  panelMediaType: 'IMAGE' | 'VIDEO' | 'MIX';
}

export interface FullAutoChapterTaskDetail {
  input: FullAutoTaskInput;
  subtasks: AdminSubtask[];
  progress: number;
  error?: string;
  targetId?: string;
}

export interface AdminSubtask {
  id: string;
  name: string;
  status: AdminTaskStatus;
  message?: string;
  startedAt?: string | Date;
  endedAt?: string | Date;
  result?: any;
}

export interface AdminTaskDetail<TPayload = any, TResult = any> {
  payload?: TPayload;
  targetId?: string;
  progress: number;
  subtasks: AdminSubtask[];
  result?: TResult;
  error?: string;
  [key: string]: any; // Cho phép thêm các field khác như 'input'
}

export interface AdminTask<TDetail = AdminTaskDetail> {
  id: string;
  type: AdminTaskType | string;
  status: AdminTaskStatus;
  detail: TDetail;
  createdAt: string;
  modifiedAt: string;
  startedAt?: string;
  endedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface AudioAsset {
  id: string;
  type: 'music' | 'sound_effect';
  name: string;
  description?: string;
  url: string;
  duration: number;
  fileSize: number;
  format: string;
  category?: string;
  tags: string[];
  bpm?: number;
  musicKey?: string;
  mood: string[];
  loopable: boolean;
  intensity?: string;
  defaultVolume: number;
  aiAnalyzed: boolean;
  aiAnalysisData?: any;
  previewUrl?: string;
  waveformUrl?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'PROCESSING';
  createdAt: string;
  modifiedAt: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface CreateAudioRequest {
  type: 'music' | 'sound_effect';
  name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  analyzeWithAI?: boolean;
}

export interface UpdateAudioRequest {
  name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  bpm?: number;
  musicKey?: string;
  mood?: string[];
  loopable?: boolean;
  intensity?: string;
  defaultVolume?: number;
}

export interface ChapterStatus {
  processes: Array<{
    id: string;
    name: string;
    videoForm: 'LONG' | 'SHORT';
    hasEpisode: boolean;
    hasVideo: boolean;
  }>;
  activeTasks: Array<{
    id: string;
    type: string;
    status: string;
    videoForm?: 'LONG' | 'SHORT';
  }>;
}

export type ChapterStatusMap = Record<string, ChapterStatus>;
